import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router';

const STORAGE_KEY = 'lifeserver-cms_url_flags';
const URL_FLAG_QUERY_PARAM = 'preview';

function parseStoredFlags(): string[] {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    try {
        const parsed: unknown = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter(
                  (f): f is string => typeof f === 'string' && f.length > 0,
              )
            : [];
    } catch {
        return [];
    }
}

function subscribeToStorage(callback: () => void) {
    const handler = (e: StorageEvent) => {
        if (e.key === STORAGE_KEY || e.key === null) callback();
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
}

function getStorageSnapshot(): string {
    return localStorage.getItem(STORAGE_KEY) ?? '';
}

export function useUrlFlags() {
    const location = useLocation();

    // React to cross-tab localStorage changes
    const storageSnapshot = useSyncExternalStore(
        subscribeToStorage,
        getStorageSnapshot,
    );

    const urlFlags = useMemo(() => {
        const searchParams = new URLSearchParams(location.search);
        const previewFlags = searchParams
            .getAll(URL_FLAG_QUERY_PARAM)
            .flatMap((v) => v.split(','))
            .map((v) => v.trim())
            .filter((v) => v.length > 0);

        const storedFlags = parseStoredFlags();

        if (previewFlags.length === 0) {
            return storedFlags;
        }

        const nextFlags = new Set(storedFlags);
        for (const flag of previewFlags) {
            if (flag === 'off') {
                nextFlags.clear();
                continue;
            }
            if (flag.startsWith('off:')) {
                const toRemove = flag.slice(4);
                if (toRemove.length > 0) nextFlags.delete(toRemove);
                continue;
            }
            nextFlags.add(flag);
        }

        const result = Array.from(nextFlags);
        if (result.length === 0) {
            localStorage.removeItem(STORAGE_KEY);
        } else {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(result));
        }
        return result;
    }, [location.search, storageSnapshot]);

    const isUrlFlagEnabled = useCallback(
        (flag: string) => urlFlags.includes(flag),
        [urlFlags],
    );

    const clearUrlFlags = useCallback(() => {
        localStorage.removeItem(STORAGE_KEY);
        // Trigger re-render in the current tab (storage events only fire cross-tab)
        window.dispatchEvent(new StorageEvent('storage', { key: STORAGE_KEY }));
    }, []);

    return { isUrlFlagEnabled, urlFlags, clearUrlFlags };
}
